import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import { parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";
import type { PatientPharmaciesResponse, PatientPharmacyAddRequest } from "@/types/dosespot";
import { addPatientPharmacy, getPatientPharmacies, removePatientPharmacy, syncPatientReportedPharmacy } from "@/api/dosespot/patient-pharmacies";
export const patientPharmacyQueryKeys = {
  all: ["dosespot", "patient", "pharmacies"] as const,
  patient: (patientId: string) => [...patientPharmacyQueryKeys.all, patientId] as const
};
export const patientPharmacyQueryOptions = {
  patient: (patientId: string, enabled: boolean) => ({
    queryKey: patientPharmacyQueryKeys.patient(patientId),
    queryFn: () => getPatientPharmacies(patientId),
    enabled: enabled && !!patientId
  })
};
export function usePatientPharmaciesQuery(patientId, enabled) {
  const $ = _c(3);
  let t0;
  if ($[0] !== enabled || $[1] !== patientId) {
    t0 = patientPharmacyQueryOptions.patient(patientId, enabled);
    $[0] = enabled;
    $[1] = patientId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useQuery(t0);
}

/**
 * Every pharmacy write returns the refreshed lists, so we prime the query cache with them
 * directly rather than triggering another DoseSpot round-trip.
 */
function cachePharmacies(patientId: string, data: PatientPharmaciesResponse | null) {
  if (data) {
    queryClient.setQueryData(patientPharmacyQueryKeys.patient(patientId), data);
  }
}
async function toastPharmacyError(title: string, error: Error) {
  toast({
    title,
    description: await parseError(error),
    variant: "error"
  });
}

/** Sync a form-reported pharmacy (by answer id) into DoseSpot. */
export function useSyncReportedPharmacyMutation(patientId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== patientId) {
    t0 = {
      mutationFn: answerId => syncPatientReportedPharmacy(patientId, {
        answerId
      }),
      onSuccess: data => {
        cachePharmacies(patientId, data);
        toast({
          title: "Pharmacy synced",
          description: "The pharmacy was added to e-prescribing.",
          variant: "success"
        });
      },
      onError: _temp
    };
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

/** Add a pharmacy staff picked from search to the patient's DoseSpot record. */
function _temp(error) {
  return toastPharmacyError("Failed to sync pharmacy", error);
}
export function useAddPatientPharmacyMutation(patientId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== patientId) {
    t0 = {
      mutationFn: request => addPatientPharmacy(patientId, request),
      onSuccess: data => {
        cachePharmacies(patientId, data);
        toast({
          title: "Pharmacy added",
          description: "The pharmacy was added to the patient's chart.",
          variant: "success"
        });
      },
      onError: _temp2
    };
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

/** Remove a pharmacy from the patient's DoseSpot record. */
function _temp2(error) {
  return toastPharmacyError("Failed to add pharmacy", error);
}
export function useRemovePatientPharmacyMutation(patientId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== patientId) {
    t0 = {
      mutationFn: pharmacyId => removePatientPharmacy(patientId, pharmacyId),
      onSuccess: data => {
        cachePharmacies(patientId, data);
        toast({
          title: "Pharmacy removed",
          description: "The pharmacy was removed from the patient's chart.",
          variant: "success"
        });
      },
      onError: _temp3
    };
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp3(error) {
  return toastPharmacyError("Failed to remove pharmacy", error);
}